import * as React from 'react';
import { useMemo, useState } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { Loader } from 'semantic-ui-react';

import { RowColor } from '../../../components/Table/Table';
import TablePagination from '../../../components/Table/TablePagination';
import { Dataset, DatasetsResponse } from '../../../shared/api/api';
import { EnhancedQueryResult, SEARCH_RESULTS_LIMIT } from '../../../shared/api/apiHooks';

import { FilterPaginationResult, useFilterSortOrder } from '../Filters/auxiliaryFields';
import { getDatasetsListColumns } from './DatasetsListTableColumns';
import { getColumnTransform } from '../DataTableCommon/DataTableCommon';
import { getSingleDatasetPath } from '../dataExplorerRouterPaths';
import isEqual from 'lodash/isEqual';
import { useDataExplorerTrackCustomEvent } from '../Filters/dataExplorerAnalytics';
import { FiltersControl } from '../Filters/types';
import { useDataExplorerHistory } from '../dataExplorerHistoryContext';
import { getCoreRowModel, getExpandedRowModel, getGroupedRowModel, Row, SortingState, useReactTable } from '@tanstack/react-table';
import { DataExplorerResultTableMarkup } from '../ResultTableMarkup/ResultTableMarkup';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';

type Props = {
  datasetQueryResult: EnhancedQueryResult<DatasetsResponse, Error>;
  control: FiltersControl;
  filterPagination: FilterPaginationResult;
  isSearchActive: boolean;
  isPyramidMultiplierVisible: boolean;
} & InjectedIntlProps;

const DatasetListTableWithoutIntl = React.memo(
  ({ intl, datasetQueryResult, control, filterPagination, isSearchActive, isPyramidMultiplierVisible }: Props) => {
    const { isFetching, isError, data, isSearchLimitExceeded } = datasetQueryResult;
    const { sortOrder, handleOrderChange, isOrderDisabled } = useFilterSortOrder({ control, isSearchActive });
    const history = useDataExplorerHistory();

    const transformColumn = React.useMemo(
      () => getColumnTransform<Dataset>({ intl, sortOrder, handleOrderChange, isOrderDisabled }),
      [intl, sortOrder, isOrderDisabled]
    );
    const trackEvent = useDataExplorerTrackCustomEvent();

    const tableColumns = React.useMemo(() => {
      return getDatasetsListColumns(trackEvent, isPyramidMultiplierVisible).map(transformColumn);
    }, [transformColumn]);

    const { page, handlePageChange, handlePageSizeChange, pageSize } = filterPagination;

    const getTrProps = React.useCallback(
      (state: any, rowInfo?: Row<Dataset>, column?: undefined) => {
        return {
          // @ts-ignore
          ...RowColor(state, rowInfo, column),
          onClick: (ev: React.MouseEvent<HTMLElement>) => {
            const tagName = (ev.target as HTMLElement).tagName;
            if (tagName === 'A') return;

            const datasetId = rowInfo?.original.id as string;
            history.push(getSingleDatasetPath({ datasetId }));
          },
        };
      },
      [history]
    );

    // Calculate effective count for pagination when search is active
    const resultCount = data?.count ?? 0;
    const effectiveCount = isSearchActive ? Math.min(resultCount, SEARCH_RESULTS_LIMIT) : resultCount;
    const totalPages = Math.ceil(effectiveCount / pageSize);
    const isSearchLimitWarningShown = isSearchActive && resultCount > SEARCH_RESULTS_LIMIT;

    const Pagination = useMemo(
      () =>
        TablePagination({
          hasButtons: false,
          numOfResults: effectiveCount,
          pageSizeLimit: 50,
          pageSize: pageSize,
          page: page,
          pages: totalPages,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
          isSearchLimitWarningShown: isSearchLimitWarningShown,
        }),
      [effectiveCount, page, pageSize, totalPages, isSearchLimitWarningShown]
    );

    const [sorting, setSorting] = useState<SortingState>([]);

    const table = useReactTable({
      columns: tableColumns,
      data: data?.results || [],
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      enableSortingRemoval: false,
      manualSorting: true,
      getGroupedRowModel: getGroupedRowModel(),
      getExpandedRowModel: getExpandedRowModel(),
    });

    const headerGroups = table.getHeaderGroups();
    const isDataLoaded = !!data;

    // show loader when data load for the first time
    if (isFetching && !data) {
      return (
        <div className="loader">
          <Loader active={true} inline="centered" size="massive" />
        </div>
      );
    }

    if (isSearchLimitExceeded) {
      return (
        <div className="agreements__content full-height">
          <div className="agreements__content-scroll full-height">
            <div className="agreements__not-available full-height">
              <FormattedMessage id="dataExplorer.searchLimitError" />
            </div>
          </div>
        </div>
      );
    }

    if (isError || !data) {
      return (
        <div className="agreements__content full-height">
          <div className="agreements__content-scroll full-height">
            <div className="agreements__not-available full-height">
              <FormattedMessage id="dataExplorer.datasets.error" />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="data-table__container">
        <DataExplorerResultTableMarkup<Dataset>
          isDataLoaded={isDataLoaded}
          isFetching={isFetching}
          headerGroups={headerGroups}
          sorting={sorting}
          table={table}
          getTrProps={getTrProps}
          Pagination={Pagination}
          noDataText={<FormattedMessageHelper id={'dataExplorer.datasets.noDatasets'} />}
        />
      </div>
    );
  }
);

export const DatasetsListTable = injectIntl(
  React.memo(DatasetListTableWithoutIntl, (prevProps, nextProps) => isEqual(prevProps, nextProps))
);
